import PropTypes from 'prop-types';
import * as R from 'ramda';
import React  from 'react';

import {createValidationContext} from 'iss/widgets/form/validation/context';

/** Provides a form and validation context and API to its children.
 * The form context is used by all the inputs and selectors in this directory
 * to query and update values in the form.
 *
 * This is done by the way of supplying a value path.
 *
 * Simplified example using the Form component in widgets/Forms.jsx:
 *
 * <Form values={someValueState} valueUpdater={updateSomeValueState} >
 *  <Input valuePath="firstname" />
 *  <Input valuePath="surname" />
 *  <Input valuePath="age" />
 * </Form>
 *
 * The above form works on a state object with the keys firstname, surname and age
 * and the input fields automatically update those keys.
 *
 * The valuepath can also be more complex. It can be an array or a dot separated path:
 *
 *  <Input valuePath="creator.firstname" />
 *  <Input valuePath={["partyGoers", index, "firstname"]} />
 *
 *  In these examples a subobject or an array is used to store the data in these fields.
 *
 * props:
 * values: the values this form operates on.
 * valueUpdater: a function that when given a transformer function from values to
 *               new values updates the state provided in the values prop.
 * children: A render function taking values and the form API as parameter
 * onSubmit: callback to fire on succesful form submit. Is given the current values and form API as a parameters.
 * onError: callback to fire on invalid form submit. Is given the current values and form API as a parameters.
 *
 * formContext aka. formAPI:
 *  submit: Submits the form, takes an optional event that it cancels
 *  validate: Runs the validation process and returns if the form is valid
 * Value API
 *  getValue(key): Extracts the value at key from the values object
 *  setValue(key, value): Updates the value at the given key to the given value
 *  updateValue(key, updater): Updates the value at the given key with the given mapper function
 * Internal API
 *  hasChanged(key): returns true if the value pointed by the key has changed in this render
 */
export class FormContext extends React.Component {
  static childContextTypes = {
    formApi: PropTypes.object.isRequired,
  };
  constructor(props) {
    super(props);
    const [validate, ValidationProvider] = createValidationContext();
    this.validate = validate;
    this.ValidationProvider = ValidationProvider;
  }
  state = {
    triedSubmittingErrors: false,
  };
  componentDidUpdate() {
    this.previousValues = this.props.values;
  }
  getValue = (path) => R.path(path, this.props.values);
  setValue = (path, value) => {
    this.updateValue(path, R.always(value));
  };
  updateValue = (path, updater) => {
    this.props.valueUpdater(R.over(R.lensPath(path), updater));
  };
  hasChanged = (path) => {
    return this.getValue(path) !== R.path(path, this.previousValues);
  };
  formApi = {
    //Value API
    getValue: (key) => this.getValue(normalizePath(key)),
    setValue: (key, value) => this.setValue(normalizePath(key), value),
    updateValue: (key, updater) => this.updateValue(normalizePath(key), updater),
    hasChanged: (key) => this.hasChanged(normalizePath(key)),
    //Other API
    submit: (e) => this.onSubmit(e),
    validate: () => this.validate(),
  };
  getChildContext = () => ({ formApi: this.formApi });
  onSubmit = (e) => {
    e && e.preventDefault();
    const {onSubmit, onError, values} = this.props;
    const valid = this.validate();

    if (valid) {
      this.setState({triedSubmittingErrors: false});
      onSubmit(values, this.formApi);
    } else {
      this.setState({triedSubmittingErrors: true});
      if (onError) {
        onError(values, this.formApi);
      }
    }
  };
  render() {
    const {ValidationProvider} = this;
    return <ValidationProvider showErrors={this.state.triedSubmittingErrors}>
      {this.props.children(this.props.values, this.formApi)}
    </ValidationProvider>;
  }
}

function normalizePath(path) {
  return R.chain(s => R.is(String, s) ? s.split(".") : s, R.flatten([path]))
    .filter(item => item != null);
}



// WEBPACK FOOTER //
// ./modules/issFrontend/widgets/form/FormContext.jsx